import { useState } from 'react';
import type { Comment } from '../../api/types';
import { useWorkshopStore } from '../../store/workshopStore';
import { Block, cx, ui } from '../../components/ui/primitives';
import s from './steps.module.css';

/**
 * Internal notes for Deliver and Roadmap. These are never rendered in the
 * Go Live view: the customer is looking at that screen.
 */
// A shared constant, not a fresh `[]` per render: the store selector's result
// has to be reference-stable or useSyncExternalStore re-renders forever.
const NO_COMMENTS: Comment[] = [];

export function Comments({ stepIndex, author }: { stepIndex: number; author: string }) {
  const comments = useWorkshopStore((st) => st.state?.comments[stepIndex] ?? NO_COMMENTS);
  const addComment = useWorkshopStore((st) => st.addComment);
  const [draft, setDraft] = useState('');

  function submit() {
    const text = draft.trim();
    if (!text) return;
    const comment: Comment = { who: author, text, ts: new Date().toISOString() };
    addComment(stepIndex, comment);
    setDraft('');
  }

  const inputId = `comment-input-${stepIndex}`;

  return (
    <Block
      icon="chat"
      title="Comments"
      lead="Use this notes panel to capture what stakeholders say as you present, such as their reactions, concerns, or answers to your questions. It stays private to you and should not show on the screen you're sharing with the customer."
    >
      <div className={s.commentList}>
        {comments.length === 0 ? (
          <p className={ui.blockLead} style={{ margin: 0 }}>
            No comments yet.
          </p>
        ) : (
          comments.map((c, i) => (
            <div key={`${c.ts}-${i}`} className={s.commentItem}>
              <div className={s.commentMeta}>
                {c.who} &middot; {new Date(c.ts).toLocaleString()}
              </div>
              <div className={s.commentText}>{c.text}</div>
            </div>
          ))
        )}
      </div>
      <label className="srOnly" htmlFor={inputId}>
        Add a note for this step
      </label>
      <textarea
        id={inputId}
        className={ui.textarea}
        placeholder="Add a note..."
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      />
      <button
        type="button"
        className={cx(ui.btn, ui.primary)}
        style={{ marginTop: 8 }}
        onClick={submit}
        disabled={draft.trim().length === 0}
      >
        Add comment
      </button>
    </Block>
  );
}
